import React from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useRouter } from 'next/router';

import { cn } from '@blockworks/ui/utils';

interface MobileSearchOption {
    name: string;
    href: string;
    slug: string;
}

const MobileMenu = ({
    searchOptions,
    searchTerm,
    setSearchTerm,
    selectedOption,
    setIsSearchActive,
}: {
    searchOptions: MobileSearchOption[];
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    selectedOption: MobileSearchOption | undefined;
    setIsSearchActive: (value: boolean) => void;
}) => {
    const router = useRouter();

    const filteredOptions =
        searchTerm === ''
            ? searchOptions
            : searchOptions.filter(option => option.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <Combobox value={searchTerm}>
            <div className="relative w-64">
                <Combobox.Input
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-md focus:outline-none focus:ring-1 focus:ring-brand"
                    onChange={event => {
                        setIsSearchActive(true);
                        setSearchTerm(event.target.value);
                    }}
                    placeholder={selectedOption?.name ?? 'Search...'}
                    value={searchTerm}
                />

                <Combobox.Button className="absolute inset-y-0 right-2 flex items-center">
                    <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
                </Combobox.Button>

                <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                >
                    <Combobox.Options className="absolute p-4 z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md overflow-auto border border-gray-300">
                        {filteredOptions.length === 0 ? (
                            <div className="p-2 text-gray-500 text-sm">No results found.</div>
                        ) : (
                            filteredOptions.map(option => (
                                <Combobox.Option
                                    key={option.name}
                                    value={option.name}
                                    className={cn(
                                        'cursor-pointer flex items-start hover:bg-gray-50 p-2 text-md font-medium',
                                        option.slug === selectedOption?.slug
                                            ? 'text-black !font-semibold'
                                            : 'text-gray-400',
                                    )}
                                    onClick={() => {
                                        setIsSearchActive(true);
                                        setSearchTerm('');
                                        router.push(option.href);
                                    }}
                                >
                                    {option.name}
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default MobileMenu;
